<template>
  <div class="bet-input">
    <div class="inputs">

      <!-- Miktar -->
      <label>
        <img src="@/assets/img/trade/wallet.svg" class="coin-small2" />
         {{ $t('turbo.amount') }}
      </label>
      <div class="amount-input">
        <img :src="coinIcon" alt="coin" class="coin-icon" />
        <input type="number" v-model="amount" min="1" />
        <span class="fiat-symbol">{{ fiatSymbol }}</span>
      </div>

      <!-- Quick action buttons -->
      <div class="amount-actions">
        <button @click="halveAmount">
          <img src="@/assets/img/trade/half.svg" class="action-icon" />
        </button>
        <button @click="doubleAmount">
          <img src="@/assets/img/trade/double.svg" class="action-icon" />
        </button>
        <button @click="increaseAmount">
          <img src="@/assets/img/trade/up2.svg" class="action-icon" />
        </button>
        <button @click="decreaseAmount">
          <img src="@/assets/img/trade/down2.svg" class="action-icon" />
        </button>
      </div>

      <!-- Kaldıraç -->
      <label>
        <img src="@/assets/img/trade/leverage.svg" class="coin-small2" />
         {{ $t('turbo.leverage:') }} {{ leverage }}x
      </label>
      <div class="slider-wrapper">
        <input type="range" min="1" max="1000" v-model="leverage" class="slider" />
      </div>

      <!-- Stop Loss -->
      <label>
        <img src="@/assets/img/trade/stoploss.svg" class="coin-small2" />
         {{ $t('turbo.Stop Loss') }} (%)
      </label>
      <div class="amount-input">
        <input type="number" v-model.number="stopLossPercent" />
        <span class="fiat-symbol">%</span>
      </div>

      <!-- Take Profit -->
      <label>
        <img src="@/assets/img/trade/takeprofit.svg" class="coin-small2" />
         {{ $t('turbo.Take Profit') }} (%)
      </label>
      <div class="amount-input">
        <input type="number" v-model.number="takeProfitPercent" />
        <span class="fiat-symbol">%</span>
      </div>
    </div>

 

    <!-- İşlem Butonları -->
    <div class="actions">
  <div class="btn-wrapper">
    <button class="long" @click="openPosition('LONG')" :disabled="isLoading">
      <img src="@/assets/img/trade/long.svg" class="btn-icon" />   {{ $t('turbo.Long') }}
    </button>

    <!-- Overlay sadece isLoading true iken -->
    <div v-if="isLoading" class="loading-overlay">
      <img src="@/assets/img/trade/loading.svg" class="loading-spinner" />
    </div>
  </div>

  <div class="btn-wrapper">
    <button class="short" @click="openPosition('SHORT')" :disabled="isLoading">
      <img src="@/assets/img/trade/short.svg" class="btn-icon" />   {{ $t('turbo.Short') }}
    </button>

    <div v-if="isLoading" class="loading-overlay">
      <img src="@/assets/img/trade/loading.svg" class="loading-spinner" />
    </div>
  </div>
</div>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue"
import { useStore } from "vuex"

const props = defineProps({
  symbol: { type: String, required: true },
  socket: { type: Object, required: false },
  user: { type: Object, required: true }
})

const fiatMap = {
  TRY: "₺",
  USD: "$",
  EUR: "€",
  USDT: "₮",
  CNY: "¥",
  INR: "₹",
  IDR: "Rp"
}

const fiatSymbol = computed(() => {
  return fiatMap[props.user.currency.fiatCurrency] || props.user.currency.fiatCurrency
})

/* --- Kullanıcının aktif wallet coin ikonu --- */
const coinIcon = computed(() => {
  const coin = props.user.currency.coinType.toUpperCase()
  return require(`@/assets/img/coins/${coin}.svg`)
})

/* --- State --- */
const amount = ref(1)
const leverage = ref(1)

const entryPrice = ref(null)
const currentPrice = ref(null)
const positionDir = ref(null)

/* --- SL & TP --- */
const stopLossPercent = ref(0)
const takeProfitPercent = ref(0)

/* --- Risk hesapları --- */
const positionSize = computed(() => amount.value * leverage.value)

const pnl = computed(() => {
  if (!entryPrice.value || !currentPrice.value || !positionDir.value) return 0
  const diff = (currentPrice.value - entryPrice.value) / entryPrice.value
  const raw = diff * positionSize.value
  return positionDir.value === "LONG" ? raw : -raw
})

const liquidationPrice = computed(() => {
  if (!entryPrice.value || !positionDir.value) return null
  if (positionDir.value === "LONG") {
    return entryPrice.value * (1 - 1 / leverage.value)
  } else {
    return entryPrice.value * (1 + 1 / leverage.value)
  }
})

const calcStopLoss = computed(() => {
  const base = entryPrice.value || currentPrice.value
  if (!base) return null
  return base * (1 - stopLossPercent.value / 100)
})

const calcTakeProfit = computed(() => {
  const base = entryPrice.value || currentPrice.value
  if (!base) return null
  return base * (1 + takeProfitPercent.value / 100)
})

/* --- Coin Icon --- */
function iconUrl(base) {
  return `https://cdn.jsdelivr.net/gh/vadimmalykhin/binance-icons/crypto/${base.toLowerCase()}.svg`
}
const chartBase = computed(() => {
  if (!props.symbol) return ""
  return props.symbol.replace(/USDT|BUSD|USD|TRY|EUR|USDC$/i, "")
})
const chartCoinIcon = computed(() => {
  return iconUrl(chartBase.value)
})

/* --- Quick buttons --- */
function halveAmount() {
  if (amount.value > 1) amount.value = Math.max(1, Math.floor(amount.value / 2))
}
function doubleAmount() {
  amount.value = amount.value * 2
}
function increaseAmount() {
  amount.value = amount.value + 1
}
function decreaseAmount() {
  if (amount.value > 1) amount.value = amount.value - 1
}

const isLoading = ref(true)

/* --- Binance Price WS --- */
let ws = null
async function fetchInitialPrice(symbol) {
  try {
    const res = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${symbol.toUpperCase()}`)
    const data = await res.json()
    currentPrice.value = parseFloat(data.price)
    isLoading.value = false  // REST yanıtıyla overlay hemen kapanır
  } catch (err) {
    console.error("REST fiyat alınamadı:", err)
  }
}

function connectWS() {
  if (ws) ws.close()
  isLoading.value = true  // coin değişince overlay başlasın

  if (!props.symbol) return

  // hemen REST ile fiyat al
  fetchInitialPrice(props.symbol)

  const stream = props.symbol.toLowerCase() + "@trade"
  ws = new WebSocket(`wss://stream.binance.com:9443/ws/${stream}`)

  ws.onmessage = (ev) => {
    const data = JSON.parse(ev.data)
    currentPrice.value = parseFloat(data.p)

    // sadece ilk tick için gerekliyse overlay'i kapat
    if (isLoading.value) {
      isLoading.value = false
    }
  }
}


onMounted(connectWS)
onBeforeUnmount(() => { if (ws) ws.close() })
watch(() => props.symbol, connectWS)

/* --- Position Open --- */
const store = useStore()
function openPosition(dir) {
  if (!currentPrice.value) return
  positionDir.value = dir
  entryPrice.value = currentPrice.value

  const payload = {
    token: store.getters.authToken,
    userId: props.user._id,
    symbol: props.symbol,
    amount: amount.value,
    leverage: leverage.value,
    entryPrice: entryPrice.value,
    direction: dir,
    stopLossPercent: stopLossPercent.value,
    takeProfitPercent: takeProfitPercent.value
  }

  
  const socket = store.getters.socketGeneral
  socket.emit("futures:open", payload, (res) => {
    
    if (res.success) {
      // ✅ Bildirim tetikle
      store.dispatch("notificationShow", {
        type: "success",
        message: `✅ ${dir} ${props.symbol} pozisyonu açıldı. Miktar: ${amount.value}${fiatSymbol.value}, Kaldıraç: ${leverage.value}x`
      })
    } else {
      store.dispatch("notificationShow", {
        type: "error",
        message: `❌ Pozisyon açılamadı: ${res.error}`
      })
    }
  })
}



/* --- Utils --- */
function fmt(v) {
  if (!v && v !== 0) return "—"
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6
  }).format(Number(v))
}
</script>

<style scoped>
.bet-input {
  background: var(--background3);
  padding: 0.8rem;
  border-radius: 7px;
  color: #f9fafb;
  max-width: 420px;
  margin: auto;
  box-shadow: 0 6px 18px rgba(0,0,0,0.3);
  font-family: "Inter", sans-serif;
  height: 100%;
}
.inputs label {
  display: block;
  margin-top: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #d1d5db;
  font-family: "Montserrat", Arial, "Segoe UI", Helvetica, sans-serif;
}
.amount-input {
  display: flex;
  align-items: center;
  background: var(--background2);
  border-radius: 6px;
  padding: 6px 10px;
  margin-top: 6px;
  position: relative;
}
.amount-input input {
  flex: 1;
  border: none;
  background: transparent;
  color: #f3f4f6;
  font-size: 14px;
  font-weight: 600;
  outline: none;
  padding: 6px;
}
.amount-input .fiat-symbol {
  margin-left: 8px;
  font-weight: 700;
  font-size: 14px;
  color: #9ca3af;
}
.amount-actions {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}
.amount-actions button {
  flex: 1;
  padding: 6px 0;
  border: none;
  border-radius: 6px;
  background: var(--background2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}
.amount-actions button:hover {
  background: #4b5563;
}
.amount-actions .action-icon {
  width: 20px;
  height: 20px;
}

.coin-icon {
  width: 20px;
    height: 20px;
    margin-right: 8px;
}
.slider-wrapper {
  margin-top: 6px;
}
.slider {
  width: 100%;
  height: 6px;
  background: var(--bar-outer);
  border-radius: 6px;
  outline: none;
  -webkit-appearance: none;
}
.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--background4);
  cursor: pointer;
}
.slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #10b981;
  cursor: pointer;
}
.btn-wrapper {
  flex: 1;
  position: relative; /* overlay için */
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  z-index: 5;
}

.loading-spinner {
  width: 28px;
  height: 28px;
  animation: spin 1s linear infinite;
}



.actions {
  margin-top: 20px;
  display: flex;
  gap: 12px;
}
.actions button {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: transform 0.1s;
}
.actions button:hover { transform: scale(1.03); }
.actions .long {  background: var(--buttongreen); 
  box-shadow: rgb(14 62 20) 0px -3px 0px 0px inset, rgb(47, 255, 57) 0px 2px 0px 0px inset;
  color: #000;  width: 100%;}
.actions .short { background: var(--button2); color: #fff; width: 100%;}
.btn-icon {
  width: 16px;
  height: 16px;
  margin-right: 6px;
}
.coin-small2 {
  width: 22px;
  height: 22px;
  vertical-align: middle;
}
</style>
